import isEmpty from "lodash/isEmpty";
import isNull from "lodash/isNull";

import { useIonRouter } from "@ionic/react";
import { loadStripe, PaymentIntent } from "@stripe/stripe-js";
import { stripeKey } from "../../firebase";
import {
  trackSignupPurchaseCompletion,
  trackSignupCompleteRegistration,
} from "../../services/marketing";
import {
  markSignUpABTestAsConverted,
  trackWebSignUpStep,
} from "../../services/user";
import { getErrorMsg, useEffectOnlyOnce } from "../../../Common/utils/common";
import { bugNotif } from "../../api/bugsnag";
import { WEB_SIGN_UP_OLD } from "../../constants/routes";
import useWebPaymentLandingStore from "./store";
import { trackSignUpCheckoutMethod } from "../../api/user";
import { flushWebSignupLogRocketSave } from "../../utils/webLogRocketRecord";

export const useWebPaymentLandingProvider = (redirectToApp?: boolean) => {
  const router = useIonRouter();

  const {
    setError,
    setPaymentMessage,
    setPaymentError,
    setSignUpName,
    setPid,
    setProductName,
    setRedirectToApp,
    setIsExpressCheckout,
    setIsOneOffEntry,
  } = useWebPaymentLandingStore();

  // const [existingUser, setExistingUser] = useState(false);
  // const [powerUp, setPowerUp] = useState(false);
  // const [error, setError] = useState("");
  // const [paymentMessage, setPaymentMessage] = useState("");
  // const [paymentError, setPaymentError] = useState("");

  useEffectOnlyOnce(() => {
    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret",
    );
    const userId = new URLSearchParams(window.location.search).get("userId");

    const pid = new URLSearchParams(window.location.search).get("pid");
    const productName = new URLSearchParams(window.location.search).get(
      "productName",
    );
    const signedUpFullName = new URLSearchParams(window.location.search).get(
      "signedUpFullName",
    );

    const expressCheckout = new URLSearchParams(window.location.search).get(
      "expressCheckout",
    );

    if (expressCheckout === "true") {
      setIsExpressCheckout(true);
    }

    const oneOffEntry = new URLSearchParams(window.location.search).get(
      "oneOffEntry",
    );

    if (oneOffEntry === "true") {
      setIsOneOffEntry(true);
    }

    console.log({ clientSecret });

    if (signedUpFullName) {
      setSignUpName(signedUpFullName.split("_").join(" "));
    }

    setPid(pid || "");
    setProductName(productName || "");

    if (
      !isEmpty(clientSecret) &&
      !isEmpty(userId) &&
      !isNull(clientSecret) &&
      !isNull(userId)
    ) {
      checkPaymentStatus(clientSecret, userId, pid || "");
    } else if (!redirectToApp) {
      router.push(WEB_SIGN_UP_OLD, "back", "pop");
    } else {
      setRedirectToApp(redirectToApp);
      if (redirectToApp) {
        setPaymentMessage("YOU ALREADY HAVE AN ACCOUNT WITH US");
      }
    }
  });

  const saveTrackSignUpCheckoutMethod = async (
    paymentIntent: PaymentIntent,
    productName: string,
    amount: number,
    promo: string | null,
    emailAddress: string | null,
  ) => {
    try {
      await trackSignUpCheckoutMethod({
        paymentIntentClientSecret: paymentIntent.id,
        subscription: productName,
        emailAddress: emailAddress || "",
        revenue: amount,
        promoCode: promo,
        express: useWebPaymentLandingStore.getState().isExpressCheckout,
      });
    } catch (error) {
      bugNotif("trackSignUpCheckoutMethod", getErrorMsg(error).rawErrorMsg);
    }
  };

  let tries = 0;
  const checkPaymentStatus = async (
    intentSecret: string,
    userId: string,
    pid = "",
    productName = "",
  ) => {
    const stripe = await loadStripe(stripeKey);
    const isOneOffEntry = useWebPaymentLandingStore.getState().isOneOffEntry;

    const showPaymentSuccessAndFlushLogRocket = () => {
      setPaymentMessage(
        isOneOffEntry ? "YOU'RE IN!" : "MEMBERSHIP UNLOCKED!",
      );
      // Payment finished on this stable landing page (no further redirect), so
      // persist the web sign-up replay stashed at create.
      flushWebSignupLogRocketSave();
    };

    if (!isNull(stripe)) {
      stripe
        .retrievePaymentIntent(intentSecret)
        .then(async ({ paymentIntent }) => {
          console.log("got payment intent --", paymentIntent);
          if (paymentIntent !== undefined) {
            switch (paymentIntent.status) {
              case "succeeded":
                showPaymentSuccessAndFlushLogRocket();
                break;

              default:
                setPaymentError("Something went wrong.");
                break; // if something went wrong, delete stripe customer and then show QR
            }

            if (paymentIntent.status === "succeeded" && !isEmpty(pid)) {
              const amount = paymentIntent.amount / 100;
              const tiktokSource = new URLSearchParams(
                window.location.search,
              ).get("ttclid");
              const emailAddress = new URLSearchParams(
                window.location.search,
              ).get("emailAddress");
              const phoneNumber = new URLSearchParams(
                window.location.search,
              ).get("phoneNumber");

              await trackSignupPurchaseCompletion({
                userId,
                subscriptionId: pid,
                value: amount,
                type: "Membership",
                transactionId: paymentIntent.id,
                tiktokSource,
                itemName: productName || undefined,
                emailAddress,
                phoneNumber,
              });

              await trackSignupCompleteRegistration({
                subscriptionId: pid,
                value: amount,
                itemName: productName || undefined,
              });

              const signUpVersion = new URLSearchParams(
                window.location.search,
              ).get("signUpVersion");

              const promoCode = new URLSearchParams(window.location.search).get(
                "promoCode",
              );

              const doubleForHalfOffAvailedParams = new URLSearchParams(
                window.location.search,
              ).get("doubleForHalfOffAvailed");
              const doubleForHalfOffAvailed =
                doubleForHalfOffAvailedParams &&
                doubleForHalfOffAvailedParams === "true"
                  ? true
                  : false;

              const promo =
                !isNull(promoCode) &&
                !isEmpty(promoCode) &&
                promoCode !== "null"
                  ? promoCode.toUpperCase().trim()
                  : null;

              const gtId = new URLSearchParams(window.location.search).get(
                "gtId",
              );
              if (gtId && !isEmpty(gtId)) {
                await markSignUpABTestAsConverted({
                  gtId,
                  identifier: emailAddress || "",
                  revenue: amount,
                  ...(doubleForHalfOffAvailedParams
                    ? {
                        metadata: {
                          doubleForHalfOffAvailed,
                        },
                      }
                    : {}),
                });
              }

              await trackWebSignUpStep({
                emailAddress: emailAddress || "",
                step: "completeSignUp",
                version: parseInt(signUpVersion || "6") as 6 | 7 | 8 | 9 | 10,
                subscription: productName,
                revenue: amount,
                promoCode: promo,
              });

              await saveTrackSignUpCheckoutMethod(
                paymentIntent,
                productName,
                amount,
                promo,
                emailAddress,
              );
            }
          } else {
            setPaymentError("Something went wrong. Please try again.");
          }
        })
        .catch((error) => {
          // Stripe already redirected here after payment; treat as success even
          // when retrievePaymentIntent fails transiently.
          showPaymentSuccessAndFlushLogRocket();

          bugNotif(
            "stripe.retrievePaymentIntent",
            getErrorMsg(error).rawErrorMsg,
          );
        });
    } else {
      setTimeout(async () => {
        tries += 1;
        if (tries < 3) {
          checkPaymentStatus(intentSecret, userId, pid);
        } else {
          setError("Something went wrong. Please contact us for support.");
        }
      }, 500);
    }
  };

  return;
};
